// ABOUTME: What kind of file an attachment is — a document, a table, slides, a track, an archive — told by its name's
// ABOUTME: extension the way the server tells it (gql-api FileTypeOf), so a file's card shows its icon and its extension.

export type FileKind = "document" | "spreadsheet" | "presentation" | "track" | "archive" | "other"

/** The extensions the server takes, by kind (gql-api docs/frontend-files.md §3). */
const KIND_BY_EXTENSION: Readonly<Record<string, FileKind>> = {
  pdf: "document",
  doc: "document",
  docx: "document",
  rtf: "document",
  odt: "document",
  txt: "document",
  xls: "spreadsheet",
  xlsx: "spreadsheet",
  ods: "spreadsheet",
  csv: "spreadsheet",
  ppt: "presentation",
  pptx: "presentation",
  fit: "track",
  gpx: "track",
  tcx: "track",
  zip: "archive",
}

/** The kind of a taken extension — its own keys only: "constructor" is no extension the server takes. */
const kindOf = (extension: string | null): FileKind | undefined =>
  extension !== null && Object.hasOwn(KIND_BY_EXTENSION, extension) ? KIND_BY_EXTENSION[extension] : undefined

/** After the last dot, in lower case; none when the dot comes first, as on the server (`model/asset_file.go:169-178`). */
export function fileExtension(name: string): string | null {
  const dot = name.lastIndexOf(".")
  const extension = dot < 1 ? "" : name.slice(dot + 1).toLowerCase()
  return extension === "" ? null : extension
}

/**
 * By the extension; by the stored content type only where the name tells nothing — which a file the server took never
 * does, since it takes a file by its extension.
 */
export function fileKind(name: string, contentType: string): FileKind {
  const byName = kindOf(fileExtension(name))
  if (byName) return byName
  if (contentType === "application/pdf" || contentType.startsWith("text/")) return "document"
  if (contentType === "application/zip") return "archive"
  return "other"
}

/** "plan.v2.pdf" → "plan.v2" and ".pdf": a card cuts the stem of a long name, never the extension. */
export function splitFileName(name: string): { stem: string; extension: string } {
  const extension = fileExtension(name)
  if (extension === null) return { stem: name, extension: "" }
  const dot = name.lastIndexOf(".")
  return { stem: name.slice(0, dot), extension: name.slice(dot) }
}

/** What the file picker offers: the extensions the server takes. "All files" in its dialog gets past it. */
export const FILE_PICKER_ACCEPT = Object.keys(KIND_BY_EXTENSION)
  .map((extension) => `.${extension}`)
  .join(",")

/** The largest file of a free person, and of a PRO athlete or a coach (gql-api docs/frontend-files.md §2): decimal. */
export const FILE_LIMIT_FREE_BYTES = 10_000_000
export const FILE_LIMIT_PAID_BYTES = 100_000_000
/** All of a PRO athlete's or a coach's files together (gql-api docs/frontend-files.md §2); a free person's take 100 MB. */
export const FILE_QUOTA_PAID_BYTES = 5_000_000_000

/** How large a file the person uploads, and how large with PRO when PRO would raise it. */
export interface FileLimits {
  limitBytes: number
  /** An athlete without PRO: a file over the limit tells what PRO takes. A coach does not buy PRO. */
  proLimitBytes: number | null
}

/**
 * The server's rule today: a PRO athlete or any coach (permissions.trainer) — the uploader's own, not the athlete's.
 * `isPaid` may be broader, never stricter: a check before the upload must not refuse what the server takes.
 */
export const fileLimitsFor = (isPaid: boolean): FileLimits =>
  isPaid
    ? { limitBytes: FILE_LIMIT_PAID_BYTES, proLimitBytes: null }
    : { limitBytes: FILE_LIMIT_FREE_BYTES, proLimitBytes: FILE_LIMIT_PAID_BYTES }

/** A file refused before its upload, for what the server would refuse it the same way: its type, no bytes, its size. */
export class FileRefusal extends Error {
  constructor(
    readonly reason: "type" | "empty" | "tooLarge",
    readonly limits: FileLimits,
  ) {
    super(reason)
    this.name = "FileRefusal"
  }
}

/**
 * The name the server takes a file's type by (gql-api `internal/model/asset_file.go` CleanFileName, used by the start
 * before FileTypeOf): no control characters, the last part of a path, trimmed — "plan.pdf " is a .pdf. Empty when it
 * names no file. Its cut to 255 characters keeps the extension, and changes no type.
 */
function cleanFileName(name: string): string {
  const path = name
    .replace(/\p{Cc}/gu, "")
    .replaceAll("\\", "/")
    .trim()
  // A path that ends in a separator names a folder.
  if (path.endsWith("/")) return ""
  return path.slice(path.lastIndexOf("/") + 1).trim()
}

/** Why the server would refuse the file, checked before it is sent anywhere — or null when it takes it. */
export function fileRefusalOf(file: File, limits: FileLimits): FileRefusal | null {
  if (kindOf(fileExtension(cleanFileName(file.name))) === undefined) return new FileRefusal("type", limits)
  if (file.size === 0) return new FileRefusal("empty", limits)
  if (file.size > limits.limitBytes) return new FileRefusal("tooLarge", limits)
  return null
}
